import { FC, useState } from 'react'
import {
  Box,
  makeStyles,
  Grid,
  FormGroup,
  Typography,
} from '@material-ui/core'
//@ts-ignore
import { useTheme } from 'styled-components'
import { countryCode } from 'common/select-options/countryCode'
import { ReactComponent as UploadIcon } from 'icons/upload.svg'
import { RequiredSign } from 'pages/content/components/jobs/components/atoms'

import { JobApplyProps } from './types'
import {
  StyledDialogContent,
  FileInput,
  AvatarContainer,
  StyledAvatar,
  ContentBody,
  UploadBox,
  StyledLabel,
  FileSelectBoxContainer,
  StyledResumeIcon
} from './styles'
import { Loader } from 'components/core/loader'
import { useAuth } from 'pages/auth/hooks/useAuth'
import extractOnboardingQuestions from 'common/utils/extractOnboardingQuestions'
import handleLongText from 'common/utils/handleLongText'
import { useFileDragEvents } from 'hooks/use-file-drag-events/useFileDragEvents'
import './job-apply-styles.css'
import useApplyJob from 'hooks/use-apply-job/useApplyJob'
import { Modal } from '../Modal'
import { InputFieldGroup } from 'common/molecules/InputFieldGroup/InputFieldGroup'
import { Select } from 'common/molecules/Select/Select'
import { Button } from 'common/atoms/Button/Button'
import { Switch } from 'common/atoms/Switch/Switch'
import { SwitchDesignModes } from 'common/atoms/Switch/Switch.types'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'

const JobApplyModal: FC<JobApplyProps> = ({ isOpen,close, title, jobId, onSuccess, companyName, companyLocation, companyLogo }) => {
  const {
    handleFileSelect,
    isLoading,
    handleInputFileClick,
    handleFormChange,
    handleFormSubmit,
    handleOnBlur,
    inputRef,
    handleUploadSelect,
    errors,
    resumeData,
    formData,
    setResumeData,
    coverLetterData,
    setCoverLetterData
  } = useApplyJob()
  const { backendUser } = useAuth()
  const { handleDrop, handleDrag, handleDragIn } = useFileDragEvents()
  const [checked, setChecked] = useState(false)
  const [fileType, setFileType] = useState('')
  
  const useStyles = makeStyles(() => ({
    select: {
      width: '200px',
      minWidth: '0px !important'
    }
  }))
  const classes = useStyles()

  const { isDevice } = useScreenSizes()

  const theme = useTheme()
 
  const existingResumeFileName = extractOnboardingQuestions({ listOfQuestions: backendUser?.onboardingQuestions, q: 'resume filename' })

  return (
    <>
      <FileInput
        type="file"
        onChange={e => handleFileSelect(e, 'upload', fileType)}
        onClick={handleInputFileClick}
        ref={inputRef}
        accept={'.pdf,.doc,.docx'}
      />
      {isOpen && backendUser && (
        <Modal 
          fullWidth 
          dialogWidth="sm" 
          showCancel={true} 
          onClose={ () => {
            close()
            setResumeData(null)
            setCoverLetterData(null)
          }} 
          show={isOpen} 
          title="Apply Now" 
          borderRadius='20px'
          borderColor={theme.colors.supplementary1}
          margin='1.625rem 1.625rem 1rem 1.625rem'
          centerTitle
          noPadding
          titleVariant='h1'
          cancelSize='1.5rem'
          showDivider={true}
          noBorder
        >
          <StyledDialogContent>
            <ContentBody>
              <AvatarContainer>
                <StyledAvatar src={companyLogo} />
                <article>
                  <Typography variant='h6'>
                    {title}
                  </Typography>
                  <Typography variant='body1' style={{ fontWeight: 'bold' }}>
                    {companyName}
                  </Typography>
                  <Typography variant='body1'>
                    {companyLocation?.replace(/, (?=$)/, '')?.replace(/, (?=$)/, '')}
                  </Typography>
                </article>
              </AvatarContainer>
              <Grid>
                <Grid item sm={12} md={12} lg={12}>
                  <InputFieldGroup
                    name="email"
                    type='email'
                    label="Email"
                    placeholder="Enter Input"
                    required
                    defaultValue={backendUser?.username}
                    onChange={handleFormChange}
                    onBlur={handleOnBlur}
                    error={
                      (formData?.email?.length > 0 && !errors?.email?.valid) ||
                      (errors?.email?.touched && !errors?.email?.valid)
                    }
                    helperText={
                      (formData?.email?.length > 0 && !errors?.email?.valid) ||
                      (errors?.email?.touched && !errors?.email?.valid)
                        ? 'Email is required'
                        : ''
                    }
                   />
                </Grid>
                <Grid item sm={12} md={12} lg={12}>
                  {' '}
                  <FormGroup>
                    <StyledLabel variant="body2">
                      Phone<RequiredSign visible={true} />
                    </StyledLabel>
                    <Grid container spacing={3}>
                      <Grid item sm={4} md={4} lg={4}>
                        <Select
                          name='countryCode'
                          width='100%'
                          placeholder="Area Code"
                          menuItemList={countryCode}
                          theme={theme}
                          multiline={false}
                          onChange={handleFormChange}
                          value={formData?.countryCode}
                        />
                      </Grid>
                      <Grid item sm={8} md={8} lg={8}>
                        <InputFieldGroup
                          name="phone"
                          type='phone'
                          placeholder="e.g 647-333-3333"
                          required
                          error={
                            (formData?.phone?.length > 0 && !errors?.phone?.valid) ||
                            (errors?.phone?.touched && !errors?.phone?.valid)
                          }
                          onChange={handleFormChange}
                          onBlur={handleOnBlur}
                          helperText={
                            (formData?.phone?.length === 0 && errors?.phone?.touched) &&
                            'Phone number is required' ||
                            (formData?.phone?.length > 0 && !errors?.phone?.valid) &&
                            'Enter valid phone number'
                          }
                        />
                      </Grid>
                    </Grid>
                  </FormGroup>
                </Grid>
                <Grid item sm={12} md={12} lg={12}>
                  <Box
                    border={`1px solid ${theme.colors.supplementary1}`}
                    borderRadius="12px"
                    padding="1rem 1rem"
                    width="100%"
                    marginTop="2.313rem"
                  >
                    <Box 
                      display="grid" 
                      gridTemplateColumns={'0.5fr 0.75fr 4.75rem'}
                      fontWeight="bold"
                      alignItems="center"
                      ml="-1rem"
                    >
                    <StyledLabel variant="body2">
                      Resume
                      <RequiredSign visible={true}/>
                    </StyledLabel>
                    <Typography variant='body2'>
                      Use existing resume from profile
                    </Typography>
                    <Switch
                      design={SwitchDesignModes.RESUME_SWITCH}
                      state={checked}
                      onChange={(isOn) => {
                        if (
                          extractOnboardingQuestions({
                            listOfQuestions: backendUser?.onboardingQuestions,
                            q: 'resume'
                          })
                        ) {
                          setChecked(!checked)
                          handleFormChange({
                            target: {
                              name: 'useExistingResume',
                              checked: isOn
                            }
                          } as any)
                        }
                      }}
                    />
                    </Box>
                    <UploadBox style={{ opacity: checked ? 0.5 : 1 }} isDevice={isDevice}>
                      {resumeData?.name && resumeData?.base64File || checked ? (
                        <>
                          <Box>
                            <Box>
                              <StyledResumeIcon />
                            </Box>
                            <Typography variant='body2'>
                              <Box fontWeight="700">
                                {handleLongText({ text: resumeData?.name, count: 20 })}
                              </Box>
                            </Typography>
                            { checked && (<Typography variant='body2'>
                              <Box fontWeight="700">
                                {handleLongText({ text: existingResumeFileName, count: 20 })}
                              </Box>
                            </Typography>)}
                            { !checked && (<Box width="10.063rem" height="2.188rem" margin=" 0.625rem auto 0 auto">
                              <Button
                                design='secondary'
                                fullWidth
                                onClick={
                                  e => {
                                    e.preventDefault()
                                    setResumeData(null)
                                  }
                                }
                              >
                                <Typography variant='body1'>
                                  <Box fontWeight="700">Remove</Box>
                                </Typography>
                              </Button>
                            </Box>)}
                          </Box>
                        </>
                      ) : (
                        <FileSelectBoxContainer
                          onDrop={e => {
                            handleDrop(e, () => handleFileSelect(e, 'drag', "resume"))
                          }}
                          onDragOver={handleDrag}
                          onDragEnter={handleDragIn}
                        >
                          <Box 
                            display="grid" 
                            gridTemplateRows="1fr 1fr 1fr" 
                            width="16rem" 
                            height="5.969rem" 
                            textAlign="center"
                            justifyContent="center"
                            justifySelf="center"
                            margin="auto"
                            gridGap="0.625rem"
                          >
                            <Box><UploadIcon fill={theme.colors.accent} /></Box>
                            <Box>
                              <Button 
                                onClick={ () => {
                                  setFileType("resume")
                                  handleUploadSelect()
                                }} 
                                design='secondary'
                              >
                                <Typography variant="body2">
                                  <Box fontWeight="700">
                                    Upload
                                  </Box>
                                </Typography>
                              </Button>
                            </Box>
                            <Typography variant="body2">Supported file types: pdf, doc (5 MB)</Typography>
                          </Box>
                        </FileSelectBoxContainer>
                      )}
                    </UploadBox>
                  </Box>
                </Grid>
                <Grid item sm={12} md={12} lg={12}>
                  <Box padding="0 1rem">
                    <Box 
                      display="grid" 
                      gridTemplateColumns={'0.5fr 0.75fr 4.75rem'}
                      fontWeight="bold"
                      alignItems="center"
                      margin="1rem 0 0 0"
                    >
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        Cover Letter
                      </Typography>
                    </Box>
                    <UploadBox isDevice={isDevice}>
                      {coverLetterData?.name && coverLetterData?.base64File ? (
                        <>
                          <Box>
                            <Box>
                              <StyledResumeIcon />
                            </Box>
                            <Typography>
                              <Box fontWeight="700">
                                {handleLongText({ text: coverLetterData?.name, count: 20 })}
                              </Box>
                            </Typography>
                            <Box width="10.063rem" height="2.188rem" margin=" 0.625rem auto 0 auto">
                              <Button
                                design='secondary'
                                fullWidth
                                onClick={
                                  e => {
                                    e.preventDefault()
                                    setCoverLetterData(null)
                                  }
                                }
                              >
                                <Typography variant='body1'>
                                  <Box fontWeight="700">Remove</Box>
                                </Typography>
                              </Button>
                            </Box>
                          </Box>
                        </>
                      ) : (
                        <FileSelectBoxContainer
                          onDrop={e => {
                            handleDrop(e, () => handleFileSelect(e, 'drag', "coverLetter"))
                          }}
                          onDragOver={handleDrag}
                          onDragEnter={handleDragIn}
                        >
                          <Box 
                              display="grid" 
                              gridTemplateRows="1fr 1fr 1fr" 
                              width="16rem" 
                              height="5.969rem" 
                              textAlign="center"
                              justifyContent="center"
                              justifySelf="center"
                              margin="auto"
                              gridGap="0.625rem"
                            >
                              <Box><UploadIcon fill={theme.colors.accent} /></Box>
                              <Box>
                                <Button 
                                  onClick={ () => {
                                    setFileType("coverLetter")
                                    handleUploadSelect()
                                  }} 
                                  design='secondary'
                                >
                                  <Typography variant="body2">
                                    <Box fontWeight="700">
                                      Upload
                                    </Box>
                                  </Typography>
                                </Button>
                              </Box>
                              <Typography variant="body2">Supported file types: pdf, doc (5 MB)</Typography>
                            </Box>
                        </FileSelectBoxContainer>
                      )}
                    </UploadBox>
                  </Box>
                </Grid>
              </Grid>

              <Box width="8rem" display="flex" margin="1.938rem 0 0 auto">
                <Button
                  design='primary'
                  forwardArrowIcon
                  onClick={() => handleFormSubmit(jobId, close, onSuccess)}
                  disabled={
                    !errors?.email?.valid ||
                    !errors?.phone?.valid ||
                    isLoading ||
                    (!resumeData?.base64File && !formData?.useExistingResume)
                  }
                  fullWidth
                >
                  {isLoading ? <Loader /> : 'Submit'}
                </Button>
              </Box>
            </ContentBody>
          </StyledDialogContent>
        </Modal>
      )}
    </>
  )
}

export default JobApplyModal